import React, { useEffect, useState } from "react";
import moment from "moment";
import Button from "../controls/button";
import CustomPopover from "../popover/Popover";
import CustomButton from "../../components/controls/button/Button";
import {
    Cross,
    PrintIcon,
    ArrowDownMedium,
    DayIcon,
    WeekIcon,
    MonthIcon,
    ListingIcon,
    CalendarIcon,
} from "../../modules/default-svg";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import styles from "./ReportButton.module.scss";
import communicationsService from "../../store/services/communications.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import { X } from "lucide-react";
import Modal from "../controls/modal/Modal";

const PARENT_ID_FIELD = {
    Client: "ClientId",
    Portfolio: "PortfolioId",
    Program: "ProgramId",
    Project: "ProjectId",
};

const DATE_RANGE_OPTIONS = [
    { key: "today", label: "Today" },
    { key: "week", label: "This Week" },
    { key: "month", label: "This Month" },
    { key: "custom", label: "Custom" },
];

const VIEW_TYPES = [
    { value: 0, label: "Day", icon: <DayIcon /> },
    { value: 1, label: "Week", icon: <WeekIcon /> },
    { value: 2, label: "Month", icon: <MonthIcon /> },
];

const DISPLAY_TYPES = [
    { value: 0, label: "List", icon: <ListingIcon /> },
    { value: 1, label: "Calendar", icon: <CalendarIcon /> },
    { value: 2, label: "Both", icon: null },
];

const getDateRange = (key) => {
    const now = moment();
    switch (key) {
        case "today":
            return { start: now.clone().startOf("day"), end: now.clone().endOf("day") };
        case "week":
            return { start: now.clone().startOf("isoWeek"), end: now.clone().endOf("isoWeek") };
        case "month":
            return { start: now.clone().startOf("month"), end: now.clone().endOf("month") };
        default:
            return null;
    }
};

export default function CalendarReportButton({ parent, parentId }) {
    const [dateRangeKey, setDateRangeKey] = useState("month");
    const [customStartDate, setCustomStartDate] = useState(null);
    const [customEndDate, setCustomEndDate] = useState(null);
    const [viewType, setViewType] = useState(1);
    const [displayType, setDisplayType] = useState(2);
    const [openPopover, setOpenPopover] = useState(false);
    const [openDateRangePopover, setOpenDateRangePopover] = useState(false);
    const [openViewTypePopover, setOpenViewTypePopover] = useState(false);
    const [openDisplayTypePopover, setOpenDisplayTypePopover] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const dispatch = useDispatch();

    const selectedDateRange = DATE_RANGE_OPTIONS.find((o) => o.key === dateRangeKey);
    const selectedViewType = VIEW_TYPES.find((v) => v.value === viewType);
    const selectedDisplayType = DISPLAY_TYPES.find((d) => d.value === displayType);

    const isCustomRangeInvalid =
        dateRangeKey === "custom" && (!customStartDate || !customEndDate || customStartDate > customEndDate);

    const closeSubPopovers = () => {
        setOpenDateRangePopover(false);
        setOpenViewTypePopover(false);
        setOpenDisplayTypePopover(false);
    };

    const handleClose = () => {
        setOpenPopover(false);
        closeSubPopovers();
    };

    const handleReport = () => {
        if (isCustomRangeInvalid) return;

        let start;
        let end;
        if (dateRangeKey === "custom") {
            start = moment(customStartDate).startOf("day");
            end = moment(customEndDate).endOf("day");
        } else {
            const range = getDateRange(dateRangeKey);
            start = range.start;
            end = range.end;
        }

        setIsLoading(true);
        const payload = {
            [PARENT_ID_FIELD[parent]]: parentId,
            StartDate: start.utc().toISOString(),
            EndDate: end.utc().toISOString(),
            DisplayType: displayType,
            ViewType: viewType,
            Tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        communicationsService
            .communicationPlanReportRequest(payload)
            .then((response) => {
                const pdfBlob = new Blob([response], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(pdfBlob);
                setReportData({
                    name: "Communication Plan Report",
                    pdfData: blobUrl,
                });
            })
            .catch((error) => {
                console.error("Report generation failed:", error);
                dispatch(
                    generalActions.showToast({
                        title: "Report generation failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setIsLoading(false);
                handleClose();
            });
    };

    useEffect(() => {
        if (!parent || !parentId) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [parent, parentId]);

    return (
        <div>
            <CustomPopover
                icon={
                    <Button
                        btnClass={`d_btn ms-12 d_btn_primary ${styles.reportButton}`}
                        icon={<PrintIcon />}
                        label="Report"
                        isDisabled={isDisabled}
                        clickHandler={() => {
                            closeSubPopovers();
                            setOpenPopover(true);
                        }}
                    />
                }
                minWidth={320}
                PopSurfaceClass="mt-1"
                status={openPopover}
            >
                <div className="text-[#342c82]">
                    <div className="d-flex justify-content-end align-items-center mb-2">
                        <div onClick={handleClose} style={{ cursor: "pointer" }}>
                            <Cross />
                        </div>
                    </div>
                    <div className={`${styles.filterPopoverBody} FilterPopoverBody`}>
                        <div className="FilterPopoverBodyScroll">
                            <div className="d-flex row align-items-center mb-3">
                                <div className="col-3">Range</div>
                                <div className="col-9 align-items-center">
                                    <CustomPopover
                                        icon={
                                            <CustomButton
                                                label={selectedDateRange.label}
                                                btnClass={`${styles.calendarBtn} d_btn`}
                                                rightIcon={
                                                    <span className="ms-6 d-inline-flex">
                                                        <ArrowDownMedium />
                                                    </span>
                                                }
                                                clickHandler={() => {
                                                    setOpenViewTypePopover(false);
                                                    setOpenDisplayTypePopover(false);
                                                    setOpenDateRangePopover((prev) => !prev);
                                                }}
                                            />
                                        }
                                        width="140px"
                                        PopSurfaceClass={styles.calendarPopover}
                                        status={openDateRangePopover}
                                    >
                                        {DATE_RANGE_OPTIONS.map((opt) => (
                                            <div
                                                key={opt.key}
                                                className={styles.calendarPopoverItem}
                                                onClick={() => {
                                                    setDateRangeKey(opt.key);
                                                    setOpenDateRangePopover(false);
                                                }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </CustomPopover>
                                </div>
                            </div>
                            {dateRangeKey === "custom" && (
                                <>
                                    <div className="d-flex row align-items-center mb-3">
                                        <div className="col-3">From</div>
                                        <div className="col-9">
                                            <DatePicker
                                                value={customStartDate}
                                                onSelectDate={setCustomStartDate}
                                                placeholder="Start date"
                                                maxDate={customEndDate || undefined}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex row align-items-center mb-3">
                                        <div className="col-3">To</div>
                                        <div className="col-9">
                                            <DatePicker
                                                value={customEndDate}
                                                onSelectDate={setCustomEndDate}
                                                placeholder="End date"
                                                minDate={customStartDate || undefined}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {/* <div className={styles.optionsDivider} /> */}
                            <div className="d-flex row align-items-center mb-3">
                                <div className="col-3">View</div>
                                <div className="col-9 align-items-center">
                                    <CustomPopover
                                        icon={
                                            <CustomButton
                                                icon={selectedViewType.icon}
                                                label={selectedViewType.label}
                                                btnClass={`${styles.calendarBtn} d_btn`}
                                                rightIcon={
                                                    <span className="ms-6 d-inline-flex">
                                                        <ArrowDownMedium />
                                                    </span>
                                                }
                                                clickHandler={() => {
                                                    setOpenDateRangePopover(false);
                                                    setOpenDisplayTypePopover(false);
                                                    setOpenViewTypePopover((prev) => !prev);
                                                }}
                                            />
                                        }
                                        width="140px"
                                        PopSurfaceClass={styles.calendarPopover}
                                        status={openViewTypePopover}
                                    >
                                        {VIEW_TYPES.map((v) => (
                                            <div
                                                key={v.value}
                                                className={styles.calendarPopoverItem}
                                                onClick={() => {
                                                    setViewType(v.value);
                                                    setOpenViewTypePopover(false);
                                                }}
                                            >
                                                {v.icon}
                                                {v.label}
                                            </div>
                                        ))}
                                    </CustomPopover>
                                </div>
                            </div>
                            <div className="d-flex row align-items-center mb-3">
                                <div className="col-3">Display</div>
                                <div className="col-9 align-items-center">
                                    <CustomPopover
                                        icon={
                                            <CustomButton
                                                icon={selectedDisplayType.icon}
                                                label={selectedDisplayType.label}
                                                btnClass={`${styles.calendarBtn} d_btn`}
                                                rightIcon={
                                                    <span className="ms-6 d-inline-flex">
                                                        <ArrowDownMedium />
                                                    </span>
                                                }
                                                clickHandler={() => {
                                                    setOpenDateRangePopover(false);
                                                    setOpenViewTypePopover(false);
                                                    setOpenDisplayTypePopover((prev) => !prev);
                                                }}
                                            />
                                        }
                                        width="140px"
                                        PopSurfaceClass={styles.calendarPopover}
                                        status={openDisplayTypePopover}
                                    >
                                        {DISPLAY_TYPES.map((d) => (
                                            <div
                                                key={d.value}
                                                className={styles.calendarPopoverItem}
                                                onClick={() => {
                                                    setDisplayType(d.value);
                                                    setOpenDisplayTypePopover(false);
                                                }}
                                            >
                                                {d.icon}
                                                {d.label}
                                            </div>
                                        ))}
                                    </CustomPopover>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-end gap-2 justify-content-end FilterPopoverFooter px-[10px]">
                        <CustomButton
                            btnClass="d_btn d_btn_white d_btn_whiteXsm"
                            label="Cancel"
                            icon={<X size={16} />}
                            clickHandler={handleClose}
                        />
                        <CustomButton
                            btnClass="d_btn d_btn_primary d_btn_primaryXsm"
                            label="Generate"
                            icon={<PrintIcon color="#fff" />}
                            clickHandler={handleReport}
                            showLoading={isLoading}
                            isDisabled={isCustomRangeInvalid}
                        />
                    </div>
                </div>
            </CustomPopover>
            {reportData && (
                <Modal
                    title={reportData.name}
                    handleCancel={() => setReportData(null)}
                    body={
                        reportData.pdfData && (
                            <iframe
                                title="PDF Viewer"
                                src={reportData.pdfData}
                                width="100%"
                                height="100%"
                            ></iframe>
                        )
                    }
                    type={true}
                    size=""
                />
            )}
        </div>
    );
}
